// Firebase for one app, straight from the Apps row — no Apps Setup import (and
// so no AdMob set) needed. Meant above all for apps made by outside
// developers: register the package in an existing project or create a new one,
// then hand them the google-services.json.
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { downloadAppGoogleServices, registerAppFirebase, getAppGa4Mp, setAppGa4Mp, setupAppGa4Mp } from '@/api/apps';
import { firebaseChoiceToInput, getFirebaseAccounts, getFirebaseOrgs, type FirebaseChoice } from '@/api/apps-setup';
import { retryProvision, getProvisionEvidence } from '@/api/firebase';
import { FirebasePicker, PROVISION_STEP_LABEL } from '@/pages/apps-setup/FirebasePicker';
import { saveBlob } from '@/lib/icon-export';
import type { App } from '@/api/types';

interface Props {
  app: App | null;
  onClose: () => void;
}

export function AppFirebaseModal({ app, onClose }: Props) {
  const queryClient = useQueryClient();
  const [choice, setChoice] = useState<FirebaseChoice | ''>('');
  const [downloadError, setDownloadError] = useState('');
  useEffect(() => { setChoice(''); setDownloadError(''); }, [app?.id]);

  const open = !!app;
  const inProgress = !!app?.firebase_provision_id;
  const linked = !!app?.firebase_project;
  const accounts = useQuery({ queryKey: ['firebase-accounts-picker'], queryFn: getFirebaseAccounts, enabled: open && !inProgress });
  const orgs = useQuery({ queryKey: ['firebase-orgs-picker'], queryFn: getFirebaseOrgs, enabled: open && !inProgress && !linked });

  // A new project takes minutes (the minute cron runs it): follow the row.
  useEffect(() => {
    if (!open || !inProgress || app?.firebase_provision_error) return;
    const t = setInterval(() => queryClient.invalidateQueries({ queryKey: ['apps'] }), 15_000);
    return () => clearInterval(t);
  }, [open, inProgress, app?.firebase_provision_error, queryClient]);

  const register = useMutation({
    mutationFn: () => registerAppFirebase(app!.id, firebaseChoiceToInput(choice || undefined)),
    onSuccess: () => { setChoice(''); queryClient.invalidateQueries({ queryKey: ['apps'] }); },
  });
  const retry = useMutation({
    mutationFn: () => retryProvision(app!.firebase_provision_id!),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['apps'] }),
  });

  const download = async () => {
    setDownloadError('');
    try {
      saveBlob(await downloadAppGoogleServices(app!.id), 'google-services.json');
    } catch (e) {
      setDownloadError(e instanceof Error ? e.message : 'Download failed');
    }
  };

  // An app already linked may only be (re)registered in its own project, so
  // the picker is narrowed to existing projects then.
  const pickerOrgs = linked ? [] : (orgs.data ?? []);

  return (
    <Modal isOpen={open} onClose={onClose} title={`Firebase — ${app?.name ?? ''}`} size="md">
      {app && (
        <div className="space-y-4 text-sm">
          <div className="rounded-md border border-border bg-surface-muted px-3 py-2 space-y-1">
            <div className="flex justify-between gap-3">
              <span className="text-content-subtle">Package</span>
              <span className="font-mono text-xs text-content">{app.bundle_id}</span>
            </div>
            <div className="flex justify-between gap-3">
              <span className="text-content-subtle">Firebase project</span>
              <span className="font-mono text-xs text-content">{app.firebase_project || '—'}</span>
            </div>
            <div className="flex justify-between gap-3">
              <span className="text-content-subtle">SDK API key</span>
              <span className="font-mono text-xs text-content-muted">{app.api_key.slice(0, 6)}…{app.api_key.slice(-3)}</span>
            </div>
          </div>

          {app.platform !== 'android' ? (
            <p className="text-content-subtle">Firebase registration here is Android-only.</p>
          ) : inProgress ? (
            app.firebase_provision_error ? (
              <div className="space-y-2">
                <p className="text-danger break-words">New Firebase project failed: {app.firebase_provision_error}</p>
                {!!app.firebase_provision_has_evidence && (
                  <button type="button" className="text-xs text-info hover:underline"
                    onClick={async () => window.open(URL.createObjectURL(await getProvisionEvidence(app.firebase_provision_id!)), '_blank')}>
                    Where the browser stopped (screenshot)
                  </button>
                )}
                <Button size="sm" loading={retry.isPending} onClick={() => retry.mutate()}>Retry from the last step</Button>
                {retry.error && <p className="text-xs text-danger">{retry.error instanceof Error ? retry.error.message : 'Retry failed'}</p>}
              </div>
            ) : (
              <p className="text-content-muted">
                Creating a Firebase project: {PROVISION_STEP_LABEL[app.firebase_provision_state ?? 'new'] ?? 'working'}… This takes a few minutes; the page updates by itself.
              </p>
            )
          ) : (
            <div className="space-y-2">
              <p className="text-content-subtle text-xs">
                {linked
                  ? 'The app is linked. Registering again in the same project re-issues its google-services.json.'
                  : 'Register the package in an existing Firebase project, or create a new project for it. No Apps Setup import or AdMob set is needed.'}
              </p>
              <div className="flex items-end gap-2">
                <FirebasePicker accounts={accounts.data ?? []} orgs={pickerOrgs} value={choice} onChange={setChoice} />
                <Button size="sm" loading={register.isPending} disabled={!choice} onClick={() => register.mutate()}>
                  {choice.startsWith('org:') || choice.startsWith('bp:') ? 'Create' : 'Register'}
                </Button>
              </div>
              {register.error && <p className="text-xs text-danger break-words">{register.error instanceof Error ? register.error.message : 'Failed'}</p>}
            </div>
          )}

          <div className="flex items-center justify-between gap-3 border-t border-border pt-3">
            <span className="text-xs text-content-subtle">
              For an outside developer: google-services.json plus the SDK API key above.
            </span>
            <Button variant="secondary" size="sm" disabled={!app.has_google_services_json} onClick={download}>
              google-services.json
            </Button>
          </div>
          {downloadError && <p className="text-xs text-danger">{downloadError}</p>}

          {app.platform === 'android' && !inProgress && <Ga4MpSection appId={app.id} />}
        </div>
      )}
    </Modal>
  );
}

const errMsg = (e: unknown, fallback: string) => (e instanceof Error ? e.message : fallback);

// Reg/dep go to Google Ads as Firebase events of this app, through the GA4
// Measurement Protocol. Its secret is normally made by us; paste one here when
// the Analytics property is not ours to administer.
function Ga4MpSection({ appId }: { appId: string }) {
  const queryClient = useQueryClient();
  const key = ['app-ga4-mp', appId];
  const status = useQuery({ queryKey: key, queryFn: () => getAppGa4Mp(appId) });
  const [secret, setSecret] = useState('');
  const [fbAppId, setFbAppId] = useState('');
  const onDone = (data: unknown) => { queryClient.setQueryData(key, data); setSecret(''); };
  const setup = useMutation({ mutationFn: () => setupAppGa4Mp(appId), onSuccess: onDone });
  const save = useMutation({
    mutationFn: () => setAppGa4Mp(appId, { api_secret: secret.trim(), firebase_app_id: fbAppId.trim() || undefined }),
    onSuccess: onDone,
  });
  const s = status.data;
  const sent = s?.sent_30d ?? {};

  return (
    <div className="border-t border-border pt-3 space-y-2">
      <div className="text-[11px] uppercase tracking-wide text-content-subtle">Google Ads conversions (Firebase)</div>
      {status.isLoading ? (
        <p className="text-xs text-content-subtle">Loading…</p>
      ) : status.error ? (
        <p className="text-xs text-danger">{errMsg(status.error, 'Failed to load')}</p>
      ) : s && (
        <>
          <div className="rounded-md border border-border bg-surface-muted px-3 py-2 space-y-1 text-xs">
            <div className="flex justify-between gap-3">
              <span className="text-content-subtle">Firebase app id</span>
              <span className="font-mono text-content break-all">{s.firebase_app_id || '—'}</span>
            </div>
            <div className="flex justify-between gap-3">
              <span className="text-content-subtle">MP secret</span>
              <span className={s.has_secret ? 'text-success' : 'text-content-muted'}>
                {s.has_secret ? `${s.secret_hint} · ${s.source === 'manual' ? 'set by hand' : 'made by us'}` : 'not set'}
              </span>
            </div>
            <div className="flex justify-between gap-3">
              <span className="text-content-subtle">Sent, 30 days</span>
              <span className="text-content">
                {sent.success ?? 0} ok · {(sent.pending ?? 0) + (sent.failed ?? 0)} retrying · {sent.dead ?? 0} dropped
              </span>
            </div>
          </div>
          {!s.has_secret && s.setup_error && <p className="text-xs text-danger break-words">Setup: {s.setup_error}</p>}
          {s.last_send_error && <p className="text-xs text-warning break-words">Last send error: {s.last_send_error}</p>}
          <div className="flex items-center gap-2">
            <Button size="sm" variant="secondary" loading={setup.isPending} onClick={() => setup.mutate()}>
              {s.has_secret ? 'Re-check secret' : 'Set up automatically'}
            </Button>
            {setup.error && <span className="text-xs text-danger break-words">{errMsg(setup.error, 'Setup failed')}</span>}
          </div>
          <div className="space-y-1">
            <p className="text-xs text-content-subtle">
              Or paste a secret from GA4 → Admin → Data streams → the app → Measurement Protocol API secrets. An empty secret goes back to automatic.
            </p>
            <div className="flex items-center gap-2">
              <input
                value={secret}
                onChange={(e) => setSecret(e.target.value)}
                placeholder="API secret"
                aria-label="Measurement Protocol API secret"
                className="block flex-1 px-2 py-1.5 border border-border-strong rounded-md sm:text-sm font-mono"
              />
              <Button size="sm" loading={save.isPending} onClick={() => save.mutate()}>Save</Button>
            </div>
            {!s.registered_firebase_app_id && (
              <input
                value={fbAppId}
                onChange={(e) => setFbAppId(e.target.value)}
                placeholder="Firebase app id, 1:…:android:… (not registered here)"
                aria-label="Firebase Android app id"
                className="block w-full px-2 py-1.5 border border-border-strong rounded-md sm:text-sm font-mono"
              />
            )}
            {save.error && <p className="text-xs text-danger break-words">{errMsg(save.error, 'Save failed')}</p>}
          </div>
        </>
      )}
    </div>
  );
}
